import React from 'react'
import type { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, type ComboboxItem } from '@node-space/ui-components'
import type { ContactEntityType, GovernmentId } from 'types/contacts'
import type { ContactFormData } from '../schemas/contactSchema'
import { GOVERNMENT_ID_COUNTRIES, MAX_GOVERNMENT_IDS } from '../utils/constants'
import { GovernmentIdCard } from './GovernmentIdCard'

type GovernmentIdsProps = {
  governmentIds: GovernmentId[]
  entityType: ContactEntityType
  errors: FieldErrors<ContactFormData>
  onAdd: () => void
  onChange: (index: number, governmentId: GovernmentId) => void
  onRemove: (index: number) => void
}

export const GovernmentIds = ({
  governmentIds,
  entityType,
  errors,
  onAdd,
  onChange,
  onRemove,
}: GovernmentIdsProps) => {
  const { t } = useTranslation()

  const governmentIdsError = errors?.governmentIds?.message ?? errors?.governmentIds?.root?.message

  const getAvailableCountryOptions = (index: number): ComboboxItem[] =>
    GOVERNMENT_ID_COUNTRIES.filter(
      country =>
        !governmentIds.some(
          (governmentId, otherIndex) =>
            otherIndex !== index && governmentId.issuingCountry === country.value
        )
    ).map(country => ({ leftText: t(country.labelKey), value: country.value }))

  return (
    <Box flexCol gapY={16} width="full" paddingB={8}>
      {governmentIdsError && (
        <Callout variant="error" testId="government-ids-array-error">
          {governmentIdsError}
        </Callout>
      )}

      {governmentIds.map((governmentId, index) => (
        <GovernmentIdCard
          key={governmentId.rowId}
          index={index}
          governmentId={governmentId}
          entityType={entityType}
          countryOptions={getAvailableCountryOptions(index)}
          errors={errors}
          onChange={updatedGovernmentId => onChange(index, updatedGovernmentId)}
          onRemove={() => onRemove(index)}
        />
      ))}

      {governmentIds.length < MAX_GOVERNMENT_IDS && (
        <Button testId="add-government-id" type="button" variant="secondary" full onClick={onAdd}>
          {t('contacts.governmentIds.addGovernmentId')}
        </Button>
      )}
    </Box>
  )
}
